/*
 * Copyright (c) 2025 Certusoft, Inc. All Rights Reserved.
 *
 * This SOURCE CODE FILE, which has been provided by Certusoft as part
 * of a Certusoft product for use ONLY by licensed users of the product,
 * includes CONFIDENTIAL and PROPRIETARY information of Certusoft.
 *
 * USE OF THIS SOFTWARE IS GOVERNED BY THE TERMS AND CONDITIONS
 * OF THE LICENSE STATEMENT AND LIMITED WARRANTY FURNISHED WITH
 * THE PRODUCT.
 */

import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core'
import * as _ from 'lodash'
import { Subject } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import { resolveReferences } from '../../../JsonReferenceResolver'
import { HttpClient } from '@angular/common/http'
import { FeatureHintInputComponent } from '../feature-hint-input/feature-hint-input.component'
import { EntityType, threedMode } from '../../types/enums'
import { ThreedUtils } from '../threed/threed-utils'

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'app-batch-process',
    templateUrl: './batch-process.component.html',
    styleUrls: ['./batch-process.component.less'],
    providers: [],
})
export class BatchProcessComponent implements OnInit, OnChanges {
    constructor(private $http: HttpClient) {}

    @Input() partsLoading!: boolean
    @Input() partsList!: string[]

    public currentPartInputSubject = new Subject<string>()
    public currentPartInput = ''
    public useRegex = false
    public regexErrors: string[] = []
    public fullPartsList: any[] = []
    public filteredPartsList: any[] = []

    // Must match the .parts-row height in the stylesheet
    public readonly rowHeight = 24
    public allSelected = false

    private ROUND_PLACE: number = 3 // Default 3
    public formatNumber: Function = ThreedUtils.getNumberFormatter(this.ROUND_PLACE)

    public recoveredJob: any = null

    ngOnInit(): void {
        this.currentPartInputSubject.pipe(debounceTime(200)).subscribe((input) => {
            this.updatePartInput(input)
        })
        this.recoveredJob = sessionStorage.getItem('tdw.batch.pending')
    }

    ngOnChanges(changes: SimpleChanges) {
        this.fullPartsList = _.map(changes.partsList.currentValue, function (value: string) {
            return { name: value, selected: false }
        })
        this.updatePartInput()
    }

    onSearch() {
        this.currentPartInputSubject.next(this.currentPartInput)
    }
    onRegexToggle() {
        this.updatePartInput()
    }
    updatePartInput(input: string = this.currentPartInput) {
        let vm = this

        vm.regexErrors = []

        if (!input || !input.trim()) {
            vm.filteredPartsList = vm.fullPartsList
            vm.updateAllSelected()
            return
        }

        // Commas and whitespace are pattern characters, so regex terms split on newlines only
        let searchTerms = _.filter(
            _.map(input.split(vm.useRegex ? /\n/ : /[,\s]/), (part) => part.trim()),
            (part) => !!part
        )

        // Patterns that fail to compile are reported and dropped from the filter
        let matchers = _.compact(
            _.map(searchTerms, function (term): ((name: string) => boolean) | null {
                if (!vm.useRegex) {
                    let lowerTerm = term.toLowerCase()
                    return (name: string) => name.toLowerCase().includes(lowerTerm)
                }
                try {
                    let pattern = new RegExp(term, 'i')
                    return (name: string) => pattern.test(name)
                } catch (e: any) {
                    vm.regexErrors.push(term + ' - ' + (e?.message || 'invalid regular expression'))
                    return null
                }
            })
        )

        vm.filteredPartsList = _.filter(vm.fullPartsList, function (x) {
            return _.some(matchers, function (matches) {
                return matches(x.name)
            })
        })

        vm.updateAllSelected()
    }

    trackByName(index: number, element: any) {
        return element.name
    }

    // Cached; called from the template it would rescan the list on every change detection pass
    updateAllSelected() {
        this.allSelected =
            this.filteredPartsList.length > 0 &&
            _.every(this.filteredPartsList, function (part) {
                return part.selected
            })
    }
    update(value: boolean, element?: any) {
        if (element) {
            element.selected = value
        } else {
            _.forEach(this.filteredPartsList, function (part) {
                part.selected = value
            })
        }
        this.updateAllSelected()
    }

    recoverJob() {
        let vm = this
        let recoveredItems = JSON.parse(vm.recoveredJob)

        _.forEach(recoveredItems, function (item) {
            _.forEach(vm.fullPartsList, function (part) {
                if (part.name === item.name) {
                    _.extend(part, item)
                }
            })
        })

        vm.recoveredJob = null

        vm.updatePartInput()
        vm.reprocessDatum(true)
    }

    async reprocessDatum(recovering?: boolean) {
        let vm = this

        let datumToProcess = _.filter(this.fullPartsList, 'selected')

        if (!recovering) {
            _.forEach(datumToProcess, (datum) => {
                datum.status = 'PENDING'
                datum.messages = []
            })
        }

        // Put job in session storage
        sessionStorage.setItem('tdw.batch.pending', JSON.stringify(datumToProcess))

        for (let i = 0; i < datumToProcess.length; i++) {
            let currentDatumObj = datumToProcess[i]
            let currentDatum = currentDatumObj.name
            let assemblyKey: string
            let partInfo: any

            if (currentDatumObj.status === 'DONE') {
                continue
            }

            let promise = new Promise(function (finalResolve, finalReject) {
                // Get Assembly
                vm.$http
                    .get('/api/clik-feature/feature-assembly/' + currentDatum)
                    .toPromise()
                    .then(
                        function (response: any) {
                            response = resolveReferences(response)
                            currentDatumObj.messages.push('Fetched Assembly')

                            // Cache assembly in renderer
                            vm.$http
                                .post('/api/renderer/cache', response.assembleResult)
                                .toPromise()
                                .then(
                                    function (response: any) {
                                        response = resolveReferences(response)
                                        currentDatumObj.messages.push('Cached Assembly')
                                        assemblyKey = response.data.key

                                        // Fetch the partInfo from CLIK for the current object
                                        vm.$http
                                            .get('/api/clik-feature/feature-info/' + currentDatum)
                                            .toPromise()
                                            .then(
                                                function (response: any) {
                                                    currentDatumObj.messages.push('Fetched Feature Info')

                                                    // Update part info object
                                                    partInfo = vm.handleGetPartInfoSuccess(response, currentDatum)

                                                    // Parse the feature hints
                                                    let promises = _.map(partInfo.datumList, function (datum) {
                                                        return new Promise(function (resolve, reject) {
                                                            FeatureHintInputComponent.parseFeatureHint(
                                                                vm.$http,
                                                                datum.featureDescription,
                                                                assemblyKey,
                                                                ThreedUtils.createVectorFromAxis(
                                                                    partInfo.preTransform.front
                                                                ),
                                                                ThreedUtils.createVectorFromAxis(
                                                                    partInfo.preTransform.up
                                                                ),
                                                                datum.featureId,
                                                                false
                                                            ).then(
                                                                function (response: any) {
                                                                    vm.handleParseFeatureHintSuccess(
                                                                        datum,
                                                                        response.data
                                                                    )
                                                                    resolve(datum)
                                                                },
                                                                function (response: any) {
                                                                    reject(response)
                                                                }
                                                            )
                                                        })
                                                    })

                                                    Promise.all(promises).then(
                                                        function (responses) {
                                                            currentDatumObj.messages.push('Parsed Feature Hints')
                                                            partInfo.datumList = responses

                                                            // Store the result on the datum object so we can batch save
                                                            currentDatumObj.datumList = partInfo.datumList

                                                            // Signal to the loop that we're done
                                                            finalResolve({})
                                                        },
                                                        function (responses: any) {
                                                            currentDatumObj.messages.push(
                                                                'Failed to parse feature descriptions',
                                                                responses
                                                            )
                                                            finalReject({})
                                                        }
                                                    )
                                                },
                                                function (response: any) {
                                                    currentDatumObj.messages.push('Failed to fetch part info', response)
                                                    finalReject({})
                                                }
                                            )
                                    },
                                    function (response: any) {
                                        currentDatumObj.messages.push('Failed to cache assembly', response)
                                        finalReject({})
                                    }
                                )
                        },
                        function (response: any) {
                            currentDatumObj.messages.push('Failed to fetch assembly', response)
                            finalReject({})
                        }
                    )
            })

            await promise.then(
                function () {
                    currentDatumObj.status = 'DONE'
                    // Update job in session storage
                    sessionStorage.setItem('tdw.batch.pending', JSON.stringify(datumToProcess))
                },
                function () {
                    currentDatumObj.status = 'ERROR'
                    // Update job in session storage
                    sessionStorage.setItem('tdw.batch.pending', JSON.stringify(datumToProcess))
                }
            )

            let batchSize = 25
            // Save a batch on every batchSize items
            if (i % batchSize === batchSize - 1 || i === datumToProcess.length - 1) {
                let datumToSave = _.filter(datumToProcess.slice(i - (i % batchSize), i + 1), function (datum) {
                    return datum.status === 'DONE'
                })

                let datumList = _.reduce(
                    datumToSave,
                    function (acc: any[], datum: any) {
                        return [...acc, ...datum.datumList]
                    },
                    []
                )

                vm.$http
                    .put('/api/clik-feature/update-features', datumList)
                    .toPromise()
                    .then(
                        function (response: any) {
                            _.forEach(datumToSave, function (datum) {
                                datum.messages.push('Saved datum entries')
                            })
                            // Update job in session storage
                            sessionStorage.setItem('tdw.batch.pending', JSON.stringify(datumToProcess))

                            if (i === datumToProcess.length - 1) {
                                // Clean session storage
                                sessionStorage.removeItem('tdw.batch.pending')
                            }
                        },
                        function (response: any) {
                            _.forEach(datumToSave, function (datum) {
                                datum.messages.push('Failed to save datum entries', response)
                            })
                        }
                    )
            }
        }
    }

    handleGetPartInfoSuccess(featureInfo: any, currentDatum: string) {
        let vm = this

        featureInfo = resolveReferences(featureInfo, true)

        let partInfo = ThreedUtils.createPartInfo()

        partInfo.name = currentDatum
        partInfo.datumList = _.map(featureInfo, function (part) {
            let datum = ThreedUtils.createDatum()
            datum.name = part.name
            datum.displayName = part.displayName
            datum.featureDescriptionOverride = part.featureDescriptionOverride
            datum.featureDescription = part.featureDescriptionOverride ? '' : part.featureDescription
            datum.featureId = part.featureId
            datum.datum = part.datum
            datum.componentSubcategories = part.componentSubcategories

            if (datum.getDatumEntityType() === EntityType.TRANSFORM && part.values.length === 16) {
                ThreedUtils.updateDatumTransformFromMatrix(datum, part.values, vm.formatNumber)
            }
            if (datum.getDatumEntityType() === EntityType.PLANE && part.values.length === 4) {
                ThreedUtils.updateDatumTransformFromPlane(datum, part.values, vm.formatNumber)
            }

            return datum
        })

        partInfo.datumList = _.sortBy(partInfo.datumList, 'name')

        return partInfo
    }

    handleParseFeatureHintSuccess(datum: any, state: any) {
        let vm = this

        datum.state = state
        // Update the status
        if (datum.state.parseSuccessful) {
            if (datum.state.logicalStructure.entityEvaluationDetails === 'OK') {
                datum.status = 'OK'
            } else {
                datum.status = 'WARNING'
            }
        } else {
            datum.status = 'ERROR'
        }

        // Update the transform if applicable
        let datumStateType = datum.getDetectedEntityType()
        datum.entityTypeMatch = datumStateType === datum.getDatumEntityType()

        if (datum?.state?.logicalStructure?.entityEvaluationSuccessful) {
            switch (datumStateType) {
                case EntityType.TRANSFORM:
                    ThreedUtils.updateDatumTransformFromMatrix(
                        datum,
                        datum.state.logicalStructure.entityData,
                        vm.formatNumber
                    )
                    break
                case EntityType.PLANE:
                    ThreedUtils.updateDatumTransformFromPlane(
                        datum,
                        datum.state.logicalStructure.entityData,
                        vm.formatNumber,
                        true
                    )
                    break
                case EntityType.HOLE_PATTERN:
                case EntityType.ARC_PAIR:
                case EntityType.SLOT:
                    ThreedUtils.updateDatumTransformFromHolePattern(datum, datum.state.logicalStructure.entityData)
                    break
                default:
                    ThreedUtils.clearDatumTransform(datum)
            }
        } else {
            // Clear out entityData, since it is referenced in the UI and we don't want endless rows of
            // arc or arc pair data crashing the client. If the entityData is to be used for something, do so before this
            if (datum?.state?.logicalStructure?.entityData) {
                datum.state.logicalStructure.entityData = []
            }
            ThreedUtils.clearDatumTransform(datum)
        }
        return datum
    }
}
